import React from 'react';
import { Notification, toaster } from 'rsuite';

/**
 * Opens a toast with the message content and status type.
 * After the duration the toast will disappear
 * @param type Type of toast to display
 * @param duration Duration that toast remains visible
 * @param placement Placement of the toast
 * @param message Content of the toast
 * @param header Toast title - If not provided, header defaults to an empty string
 */
export const openToaster = (
  type: 'info' | 'success' | 'warning' | 'error',
  duration: number,
  placement:
    | 'topStart'
    | 'topCenter'
    | 'topEnd'
    | 'bottomStart'
    | 'bottomCenter'
    | 'bottomEnd',
  message: string,
  header?: string | undefined,
) => {
  toaster.push(
    <Notification type={type} header={header || ''} closable>
      <p>{message}</p>
    </Notification>,
    { placement, duration },
  );
};

/**
 * Opens a success toast with a custom message.
 * After the duration the toast will disappear
 * @param message the message to display within the toast
 * @param duration time for toast to remain visible
 */
export const openSuccessToaster = (message: string, duration: number) => {
  toaster.push(
    <Notification type="success" closable>
      <p
        style={{
          margin: 0,
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          fontSize: 'var(--rs-notify-title-font-size, 1rem)',
          lineHeight: 'var(--rs-notify-line-height, 1.5)',
          color: 'var(--rs-text-heading)',
        }}
      >
        {message}
      </p>
    </Notification>,
    {
      placement: 'topEnd',
      duration,
    },
  );
};

/**
 * Opens an error toast with a custom message.
 * After the duration the toast will disappear
 * @param message the message to display within the toast
 * @param duration time for toast to remain visible
 */
export const openErrorToaster = (
  message: string,
  duration: number,
  header: string | undefined = 'Error',
) => {
  toaster.push(
    <Notification type="error" header={header} closable>
      <p>{message}</p>
    </Notification>,
    { placement: 'topEnd', duration },
  );
};

/**
 * Opens an info toast with a custom message.
 * After the duration the toast will disappear
 * @param message the message to display within the toast
 * @param duration time for toast to remain visible
 * @param closable whether the toast can be manually closed
 */
export const openInfoToaster = (
  message: string,
  duration: number,
  closable?: boolean,
) => {
  toaster.push(
    <Notification
      type="info"
      header="Info"
      duration={duration}
      closable={closable}
    >
      {message}
    </Notification>,
    { placement: 'topEnd', duration },
  );
};

/**
 * Opens a persistent info toast that stays visible until manually closed or removed.
 * @param message the message to display
 * @param key unique key used to later remove the toast via removeToaster()
 */
export const openPersistentInfoToaster = async (
  message: string,
  key: string,
): Promise<string> => {
  await toaster.push(
    <Notification type="info" header="Info" closable>
      {message}
    </Notification>,
    { placement: 'topEnd', duration: 0 },
  );
  return key;
};

/**
 * Removes a specific toast by key.
 * @param key the key of the toast to remove
 */
export const removeToaster = (key: string) => {
  toaster.remove(key);
};

export const clearToaster = () => {
  toaster.clear();
};
